import React, { useState, useEffect, memo, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { getCustomer, clearCustomer } from "../../../actions/customer";
import { decrypt } from "../../../utils/encodingdecoding";
import { getSchemaList, updateCustomer } from "../../../services/auth";
import swal from "sweetalert";
import Header from "../../layout/Header";
import Paginated from "./Paginated";
import { getDisplayDate } from "../../../utils/helper";

const Kyc = memo(({ getCustomer, Customer, clearCustomer }) => {
  const location = useLocation();
  const [data, setData] = useState([]);
  const [customerCount, setCustomerCount] = useState();
  const [response, setResponse] = useState([]);
  const [totalCustomer, setTotalCustomer] = useState(0);
  const [shemaData, setSchemaData] = useState();
  const [loader, setLoader] = useState(true);
  const [testAccounts, setTestAccounts] = useState(false);

  useEffect(() => {
    setResponse(Customer?.data);
  }, [Customer]);

  const updateData = () => {
    setLoader(true);
    const token = JSON.parse(localStorage.getItem("token_gen"));
    getSchemaList(token?.access_token).then((res) => {
      const schemaProperties = { ...res?.schema?.properties };
      delete schemaProperties?.profileimageurl;
      setSchemaData(schemaProperties);
    });
  };

  useEffect(() => {
    updateData();
  }, []);

  const handleKycApproval = async (val, status) => {
    setLoader(true);
    const token = JSON.parse(localStorage.getItem("token_gen"));
    const res = await updateCustomer(
      token?.access_token,
      val?.customerData?.customercode,
      {
        isykcverified: status,
        cpkycstatus: status === true ? "Approved" : "Reject",
      },
    );
    if (res?.message === "Request processed successfully.") {
      await fetch("", "", "", true);
      setLoader(false);
      swal({
        position: "center",
        icon: "success",
        title: `KYC update successfully`,
        showConfirmButton: false,
        timer: 5000,
      });
    } else {
      swal({
        position: "center",
        icon: "error",
        title: res.message,
        showConfirmButton: false,
        timer: 5000,
      });
    }
  };

  useEffect(() => {
    const temp = response?.data?.data;
    response?.data?.totalCount &&
      setTotalCustomer(response?.data?.totalCount || 0);
    if (!temp) {
      setData([]);
      return;
    }
    if (temp) {
      temp?.map((val) => {
        val.action = (
          <>
            {!Object.prototype.hasOwnProperty.call(
              val?.customerData || {},
              "isykcverified",
            ) ? (
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => {
                    handleKycApproval(val, true);
                  }}
                >
                  Approve
                </button>

                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => {
                    handleKycApproval(val, false);
                  }}
                >
                  Reject
                </button>
              </div>
            ) : (
              <Link className="edit_btn" csvalue="View Details">
                {val?.customerData?.isykcverified ? "Verified" : "Rejected"}
              </Link>
            )}
          </>
        );

        if (shemaData) {
          Object.keys(shemaData)?.forEach((key) => {
            if (key === "profileimageurl") {
              delete val[key];
              return;
            }

            if (shemaData[key]?.isFormView) {
              let value = val.customerData[key];
              if (key === "createdAt" && value) {
                value = getDisplayDate(value);
              }

              if (value === true) {
                value = "Yes";
              } else if (value === false) {
                value = "No";
              }

              val[key] = value ?? "";
            }
          });
        }

        return val;
      });
      setData(temp);
    }
  }, [response, shemaData]);

  const [search, setSearch] = useState("");
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  const fetch = async (
    testAccounts = false,
    searchText = "",
    showLoader = true,
    kyc = false,
  ) => {
    // Cancel previous API call
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      showLoader && setLoader(true);
      if (!kyc) {
        await clearCustomer();
      }

      const token = JSON.parse(localStorage.getItem("token_gen"));
      const id = localStorage.getItem("Id");

      await getCustomer(
        token?.access_token,
        id,
        1,
        50,
        searchText,
        testAccounts,
        { signal: controller.signal },
      );
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Customer fetch failed:", err);
      }
    } finally {
      showLoader && setLoader(false);
    }
  };

  const dynamicColumns = useMemo(() => {
    if (!shemaData) return [];

    return Object.entries(shemaData)
      .filter(([key, value]) => value.isFormView && key !== "isTest")
      .map(([key, value]) => ({
        Header: value.fielddisplayname || value.label || key,
        Footer: value.fielddisplayname || value.label || key,
        accessor: key,
      }));
  }, [shemaData]);

  const columns = useMemo(() => {
    const staticColumns = [
      ...dynamicColumns,
      {
        Header: "Action",
        Footer: "Action",
        accessor: "action",
      },
    ];
    return staticColumns;
  }, [dynamicColumns]);

  const searchableKeys = useMemo(() => {
    return columns
      .map((col) => col.accessor)
      .filter((key) => key && typeof key === "string" && key !== "action");
  }, [columns]);

  const objectContainsSearch = (row, query, keys) => {
    if (!row || !query) return true;

    const q = query.toLowerCase();

    return keys.some((key) => {
      const value = row[key];

      if (value === null || value === undefined) return false;

      if (typeof value === "string" || typeof value === "number") {
        return String(value).toLowerCase().includes(q);
      }

      if (Array.isArray(value)) {
        return value.some(
          (v) =>
            (typeof v === "string" || typeof v === "number") &&
            String(v).toLowerCase().includes(q),
        );
      }

      return false;
    });
  };

  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!search.trim()) {
        fetch(testAccounts, "", true);
        return;
      }

      const localMatches = data.filter((row) =>
        objectContainsSearch(row, search, searchableKeys),
      );
      if (localMatches.length > 0) {
        setData(localMatches);
      } else {
        fetch(testAccounts, search);
      }
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [search, testAccounts, searchableKeys]);

  const generateCSV = () => {
    try {
      if (!data || data.length === 0) {
        alert("No customer data available to download!");
        return;
      }
      const downloadColumns = columns?.filter(
        (col) => col.accessor !== "action",
      );
      const headers = downloadColumns.map((col) => col.Header);
      const rows = data?.map((item) => {
        return downloadColumns?.map((col) => {
          const value = item[col.accessor];
          if (value === 0) return "0";
          if (value === null || value === undefined) return "";
          return value.toString();
        });
      });
      const csvContent = [headers, ...rows]
        .map((row) => row.join(","))
        .join("\n");
      const encodedUri = encodeURI("data:text/csv;charset=utf-8," + csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "customers_data.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("CSV Download Error:", error);
      alert("Failed to generate CSV. Please try again.");
    }
  };

  useEffect(() => {
    setCustomerCount(Customer?.data?.data?.totalCount);
  }, [Customer]);

  useEffect(() => {
    return () => {
      setSearch("");
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      <Header name={"KYC Management"} />
      {loader && (
        <div className="mainPreloaderMain">
          <div className="mainPreloader">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      )}
      <div className="dashboard__content">
        <div className="container-fluid">
          <div className="dashboard__customersCard">
            <div className="dashboard__header">
              <div className="dashboard__left">
                <h4>KYC List</h4>
              </div>
              <div className="dashboard__right">
                {/* <button
                                    onClick={generateCSV}
                                    className='btn btn-default'>
                                    <i
                                        className='fa-solid fa-download'
                                        style={{ marginRight: '10px' }}></i>
                                    Download KYC
                                </button> */}
              </div>
            </div>
            <div className="dashboard__body">
              <Paginated
                data={data}
                columns={columns}
                loader={loader}
                setLoader={setLoader}
                testAccounts={testAccounts}
                search={search}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

const mapStateToProps = (state) => ({
  Customer: state.Customer,
});
const mapDispatchToProps = {
  getCustomer,
  clearCustomer,
};

export default connect(mapStateToProps, mapDispatchToProps)(Kyc);
